const DYNAMIC_OBJECT_ENABLED_TYPES = new Set(['DEAL_PLAN']);
function hrefToPathname(href) {
  return href.startsWith('/') ? href : new URL(`https://${href}`).pathname;
}
function isActiveRoute(href, currentPathname) {
  if (!href) return false;
  const hrefPath = hrefToPathname(href);
  return currentPathname === hrefPath || currentPathname.startsWith(`${hrefPath}/`);
}
function toSideNavItem(item, hrefBase, isCurrent, currentPathname) {
  var _item$children, _item$badge;
  const href = item.path ? `${hrefBase}${item.path}` : undefined;
  const children = ((_item$children = item.children) !== null && _item$children !== void 0 ? _item$children : []).slice().sort((a, b) => {
    var _a$order, _b$order;
    return ((_a$order = a.order) !== null && _a$order !== void 0 ? _a$order : 0) - ((_b$order = b.order) !== null && _b$order !== void 0 ? _b$order : 0);
  }).map(child => toSideNavItem(child, hrefBase, isCurrent, currentPathname));
  const isLeaf = children.length === 0;
  const isActive = isCurrent && (!isLeaf || !href || isActiveRoute(href, currentPathname));
  return {
    id: item.id,
    href,
    path: item.path,
    isCurrent,
    isActive,
    external: item.external,
    badgeCount: (_item$badge = item.badge) === null || _item$badge === void 0 ? void 0 : _item$badge.count,
    children
  };
}
function getHrefBase(baseUrl, isLocal) {
  return isLocal ? new URL(`https://${baseUrl}`).pathname.replace(/\/$/, '') : baseUrl;
}
function isDynamicObjectEnabledType(type) {
  return DYNAMIC_OBJECT_ENABLED_TYPES.has(type);
}
function hasDynamicObjects(entry) {
  var _entry$dynamicPortalO;
  return isDynamicObjectEnabledType(entry.portalInstanceType) && ((_entry$dynamicPortalO = entry.dynamicPortalObjects) !== null && _entry$dynamicPortalO !== void 0 ? _entry$dynamicPortalO : []).length > 0;
}
function applyDynamicPortalObjects(navItem, entry, hrefBase) {
  var _entry$dynamicPortalO2;
  const dynamicObjects = (_entry$dynamicPortalO2 = entry.dynamicPortalObjects) !== null && _entry$dynamicPortalO2 !== void 0 ? _entry$dynamicPortalO2 : [];
  if (!isDynamicObjectEnabledType(entry.portalInstanceType) || dynamicObjects.length === 0) {
    return navItem;
  }
  if (dynamicObjects.length === 1) {
    return Object.assign({}, navItem, {
      href: `${hrefBase}/${dynamicObjects[0].objectId}`,
      children: []
    });
  }
  const dynamicChildren = dynamicObjects.map(obj => ({
    id: `${navItem.id}.${obj.objectId}`,
    label: obj.displayName,
    href: `${hrefBase}/${obj.objectId}`,
    isCurrent: navItem.isCurrent,
    isActive: false,
    children: []
  }));
  return Object.assign({}, navItem, {
    children: dynamicChildren
  });
}
function countDestinations(items) {
  return items.reduce((count, item) => {
    var _item$children2;
    const children = (_item$children2 = item.children) !== null && _item$children2 !== void 0 ? _item$children2 : [];
    return count + (children.length > 0 ? children.length : 1);
  }, 0);
}
export function buildPortalNavigation({
  data,
  loading,
  isLocal,
  currentPathname
}) {
  var _data$portalNavigatio, _siteMenuData$items$l, _siteMenuData$items;
  const entries = (_data$portalNavigatio = data === null || data === void 0 ? void 0 : data.portalNavigationEntries) !== null && _data$portalNavigatio !== void 0 ? _data$portalNavigatio : [];
  const siteMenuData = data === null || data === void 0 ? void 0 : data.siteMenuData;
  const siteMenuItemCount = (_siteMenuData$items$l = siteMenuData === null || siteMenuData === void 0 || (_siteMenuData$items = siteMenuData.items) === null || _siteMenuData$items === void 0 ? void 0 : _siteMenuData$items.length) !== null && _siteMenuData$items$l !== void 0 ? _siteMenuData$items$l : 0;
  const nonUniversalEntries = entries.filter(e => e.portalInstanceType !== 'UNIVERSAL');
  const shouldPromoteChildren = nonUniversalEntries.length <= 1 && siteMenuItemCount === 0;
  const navItems = nonUniversalEntries.flatMap(entry => {
    var _entry$navigationMani;
    if (!((_entry$navigationMani = entry.navigationManifest) !== null && _entry$navigationMani !== void 0 && _entry$navigationMani.items)) return [];
    const hrefBase = getHrefBase(entry.baseUrl, isLocal);
    const entryHasDynamicObjects = hasDynamicObjects(entry);
    return entry.navigationManifest.items.slice().sort((a, b) => {
      var _a$order2, _b$order2;
      return ((_a$order2 = a.order) !== null && _a$order2 !== void 0 ? _a$order2 : 0) - ((_b$order2 = b.order) !== null && _b$order2 !== void 0 ? _b$order2 : 0);
    }).flatMap(item => {
      const baseNavItem = toSideNavItem(item, hrefBase, entry.isCurrent, currentPathname);
      const navItem = applyDynamicPortalObjects(baseNavItem, entry, hrefBase);
      if (shouldPromoteChildren && !entryHasDynamicObjects && navItem.children.length > 0) {
        return navItem.children;
      }
      return [navItem];
    });
  });
  const nonUniversalDestinationCount = nonUniversalEntries.reduce((count, entry) => {
    var _entry$navigationMani2, _entry$navigationMani3, _entry$dynamicPortalO3;
    const manifestCount = countDestinations((_entry$navigationMani2 = (_entry$navigationMani3 = entry.navigationManifest) === null || _entry$navigationMani3 === void 0 ? void 0 : _entry$navigationMani3.items) !== null && _entry$navigationMani2 !== void 0 ? _entry$navigationMani2 : []);
    const dynamicCount = hasDynamicObjects(entry) ? ((_entry$dynamicPortalO3 = entry.dynamicPortalObjects) !== null && _entry$dynamicPortalO3 !== void 0 ? _entry$dynamicPortalO3 : []).length : 0;
    return count + Math.max(manifestCount, dynamicCount);
  }, 0);
  const shouldShowSideNav = loading || nonUniversalDestinationCount > 1 || siteMenuItemCount > 0;
  return {
    navItems,
    siteMenuData,
    shouldShowSideNav
  };
}